import React, { forwardRef, AnchorHTMLAttributes } from "react";
import { ButtonProps } from "react-bootstrap/Button";
import { ActionType } from "utils/analytics/analyticsConstants";
import { useAnalytics } from "utils/analytics/AnalyticsContext";
import { AnalyticsProps } from "utils/analytics/AnalyticsProps";
import {
  $Button,
  $LinkButton,
  $LinkButtonOnDark,
  $PrimaryButton,
  $PrimaryButtonOnDark,
  $SecondaryButton,
  $SecondaryButtonOnDark,
  $IconButton,
  $IconButtonOnDark,
} from "./styled-components";
import clsx from "clsx";

export type ButtonElement = HTMLButtonElement | HTMLAnchorElement;
type Variant = "primary" | "secondary" | "link" | "icon";

export interface UIButtonProps extends ButtonProps, AnalyticsProps {
  block?: boolean;
  requestInProgress: boolean;
  variant?: Variant;
  isOnDarkBackground?: boolean;
  rel?: AnchorHTMLAttributes<HTMLAnchorElement>["rel"];
}

function styledComponentFromVariant(
  variant: Variant,
  isOnDarkBackground?: boolean,
): typeof $Button {
  switch (variant) {
    case "link":
      return isOnDarkBackground ? $LinkButtonOnDark : $LinkButton;
    case "primary":
      return isOnDarkBackground ? $PrimaryButtonOnDark : $PrimaryButton;
    case "secondary":
      return isOnDarkBackground ? $SecondaryButtonOnDark : $SecondaryButton;
    case "icon":
      return isOnDarkBackground ? $IconButtonOnDark : $IconButton;
    default:
      return $Button;
  }
}

/**
 * Design system button component.
 *
 * Please use this component for all buttons and links **except** links that navigate
 * the user to another page within the app. The `components/Navlink` component is
 * currently being used for in-app navigation.
 *
 * When navigating the user to an external url, pass an `href` prop into the Button
 * component. That will cause the Button component to use an `<a>` element instead of
 * a `<button>` element, which is important for accessibility purposes. (You can
 * still pass in an `onClick` prop in addition to the `href` prop.)
 *
 * Example:
 *
 * ```jsx
 * <Button
 *   variant="link"
 *   href="https://www.the-page-we-re-linking-to.com"
 *   {...otherPropsNotRelevantToThisConversation}
 * >
 *   Button text
 * </Button>
 * ```
 */
const Button = forwardRef<ButtonElement, UIButtonProps>((props, ref) => {
  const {
    analyticsName,
    analyticsSurface,
    analyticsProperties,
    block,
    disabled,
    onClick,
    variant = "primary",
    isOnDarkBackground = false,
    requestInProgress,
    children,
    href,
    ...restProps
  } = props;
  const { trackEvent } = useAnalytics("Button " + analyticsName);
  const ButtonComponent = styledComponentFromVariant(
    variant,
    isOnDarkBackground,
  );

  const className = [];
  if (props.className) {
    className.push(props.className);
  }

  if (block) {
    // Use a static classname so we can apply sibling styles.
    className.push("btn-block");
  }

  return (
    <ButtonComponent
      ref={ref}
      {...restProps}
      className={clsx(className)}
      href={!disabled && href ? href : undefined}
      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
        trackEvent({
          eventName: analyticsName,
          eventType: ActionType.CLICK_BUTTON,
          analyticsSurface,
          extraProperties: analyticsProperties,
        });

        if (onClick) {
          onClick(event);
        }
      }}
      disabled={disabled || requestInProgress}
    >
      {children}
    </ButtonComponent>
  );
});

Button.displayName = "Button";

export default Button;
